// pages/admin/Dashboard.tsx
/**
 * @mockup /design/mockups/05-desktop-admin-dashboard.png
 * @description Admin dashboard home with responsive stats and building overview
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building as BuildingIcon, ChevronRight, TrendingUp, TrendingDown, QrCode, Mail, FileText, Eye, Clock, Loader2, Plus } from 'lucide-react';
import { AdminLayout } from '../../components/layout';
import { Button, Modal, Input } from '../../components/ui';
import { useToast } from '../../components/ui/Toast';
import { BUILDINGS } from '../../data/buildings';
import { getApplications, createBuilding, type Application } from '../../lib/api';

// Mock stats data (would come from analytics in production)
const MOCK_STATS = [
  { label: 'Total QR Scans', value: '1,247', change: '+12%', trend: 'up', period: 'vs last month' },
  { label: 'Email Inquiries', value: '89', change: '+8%', trend: 'up', period: 'vs last month' },
  { label: 'Inquiries', value: '34', change: '+23%', trend: 'up', period: 'vs last month' },
  { label: 'Vacancy Rate', value: '4.2%', change: '-0.8%', trend: 'down', period: 'vs last month' },
];

// Helper to format relative time
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return date.toLocaleDateString();
}

// Static activity items (non-application events)
const STATIC_ACTIVITY = [
  { type: 'scan', building: 'The Eglinton', time: '5 minutes ago', icon: QrCode, applicationId: null },
  { type: 'email', building: 'The Eglinton', time: '3 hours ago', icon: Mail, applicationId: null },
  { type: 'scan', building: 'Havenbrook Gardens', time: '5 hours ago', icon: QrCode, applicationId: null },
  { type: 'view', building: 'Mountview', unit: '2-BR #405', time: '6 hours ago', icon: Eye, applicationId: null },
  { type: 'email', building: 'Lampe Towers', time: '8 hours ago', icon: Mail, applicationId: null },
];

// Use real buildings with mock engagement stats
const DASHBOARD_BUILDINGS = BUILDINGS.map((b, i) => ({
  id: b.id,
  name: b.name.split(' (')[0], // Remove parenthetical like "(Lampe Towers)"
  slug: b.slug,
  city: b.neighborhood,
  heroImage: b.heroImage,
  units: b.units.length * 50, // Mock total units
  vacant: b.units.filter(u => u.isAvailable).length,
  scans: [423, 312, 187, 156, 134, 98][i] || 100,
  inquiries: [31, 22, 14, 11, 9, 7][i] || 10,
}));

export function Dashboard() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [applications, setApplications] = useState<Application[]>([]);
  const [loadingActivity, setLoadingActivity] = useState(true);
  const [isAddBuildingOpen, setIsAddBuildingOpen] = useState(false);
  const [newBuildingData, setNewBuildingData] = useState({
    name: '',
    address: '',
    city: 'Toronto',
    postal_code: '',
    leasing_email: '',
    leasing_phone: '',
  });
  const [isCreating, setIsCreating] = useState(false);

  // Fetch recent applications
  useEffect(() => {
    async function fetchApplications() {
      setLoadingActivity(true);
      const result = await getApplications();
      if (result.data?.applications) {
        setApplications(result.data.applications);
      }
      setLoadingActivity(false);
    }
    fetchApplications();
  }, []);

  // Generate slug from name
  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  // Handle create building
  const handleCreateBuilding = async () => {
    if (!newBuildingData.name || !newBuildingData.address || !newBuildingData.leasing_email) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    setIsCreating(true);
    const slug = generateSlug(newBuildingData.name);

    const result = await createBuilding({
      ...newBuildingData,
      slug,
      postal_code: newBuildingData.postal_code || null,
      leasing_phone: newBuildingData.leasing_phone || null,
    });

    setIsCreating(false);

    if (result.error) {
      showToast(`Failed to create building: ${result.error}`, 'error');
      return;
    }

    showToast('Building created successfully', 'success');
    setIsAddBuildingOpen(false);
    setNewBuildingData({
      name: '',
      address: '',
      city: 'Toronto',
      postal_code: '',
      leasing_email: '',
      leasing_phone: '',
    });

    // Navigate to the new building
    if (result.data?.slug) {
      navigate(`/admin/buildings/${result.data.slug}`);
    }
  };

  // Convert applications to activity items and merge with static activity
  const applicationActivities = applications.slice(0, 10).map((app) => ({
    type: 'application' as const,
    building: app.buildingName,
    unit: app.unitType && app.unitNumber ? `${app.unitType} #${app.unitNumber}` : undefined,
    time: formatRelativeTime(app.submittedAt),
    icon: FileText,
    applicationId: app.id,
    timestamp: new Date(app.submittedAt).getTime(),
  }));

  // Merge and sort by timestamp (applications are real, others are static placeholders)
  const recentActivity = [
    ...applicationActivities,
    ...STATIC_ACTIVITY.map((item, i) => ({
      ...item,
      timestamp: Date.now() - (i + 1) * 3600000, // Spread static items over hours
    })),
  ]
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, 7);

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8">
        {/* Page Header */}
        <div className="mb-6 lg:mb-8">
          <h1 className="text-xl lg:text-2xl font-semibold text-gray-900">Dashboard</h1>
          <p className="text-sm lg:text-base text-gray-500 mt-1">
            Welcome back! Here's an overview of your properties.
          </p>
        </div>

        {/* Stats Grid - 2 cols on mobile, 4 on desktop */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-6 lg:mb-8">
          {MOCK_STATS.map((stat) => (
            <div
              key={stat.label}
              className="bg-white p-4 lg:p-5 rounded-xl border border-gray-200"
            >
              <p className="text-xl lg:text-2xl font-bold text-gray-900 mb-0.5 lg:mb-1">{stat.value}</p>
              <p className="text-xs lg:text-sm font-medium text-gray-500 truncate">{stat.label}</p>
              <div className="flex items-center gap-1 mt-1.5 lg:mt-2">
                {stat.trend === 'up' ? (
                  <TrendingUp size={12} className="text-green-600 flex-shrink-0" />
                ) : (
                  <TrendingDown size={12} className="text-green-600 flex-shrink-0" />
                )}
                <span className="text-xs text-green-600 font-medium">{stat.change}</span>
                <span className="text-xs text-gray-400 hidden sm:inline truncate">{stat.period}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
          {/* Buildings Section - Takes 2/3 */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-base lg:text-lg font-semibold text-gray-900">Buildings Performance</h2>
              <Button size="sm" onClick={() => setIsAddBuildingOpen(true)}>
                <Plus size={16} className="mr-1" />
                Add Building
              </Button>
            </div>

            {/* Desktop: Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Building</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Units</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Vacant</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Scans</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Inquiries</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {DASHBOARD_BUILDINGS.map((building) => (
                    <tr
                      key={building.id}
                      onClick={() => navigate(`/admin/buildings/${building.slug}`)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                            <BuildingIcon size={20} className="text-gray-500" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{building.name}</p>
                            <p className="text-sm text-gray-500">{building.city}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center text-gray-600">{building.units}</td>
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          building.vacant > 5 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {building.vacant}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center text-gray-600">{building.scans}</td>
                      <td className="px-6 py-4 text-center text-gray-600">{building.inquiries}</td>
                      <td className="px-6 py-4 text-right">
                        <ChevronRight size={18} className="text-gray-400" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile: Cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {DASHBOARD_BUILDINGS.map((building) => (
                <button
                  key={building.id}
                  onClick={() => navigate(`/admin/buildings/${building.slug}`)}
                  className="w-full p-4 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
                >
                  <img
                    src={building.heroImage}
                    alt={building.name}
                    className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{building.name}</p>
                    <p className="text-sm text-gray-500">{building.city}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs text-gray-500">{building.units} units</span>
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs">
                        {building.vacant} available
                      </span>
                    </div>
                  </div>
                  <ChevronRight size={20} className="text-gray-400 flex-shrink-0" />
                </button>
              ))}
            </div>
          </div>

          {/* Recent Activity - Takes 1/3 */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-gray-100">
              <h2 className="text-base lg:text-lg font-semibold text-gray-900">Recent Activity</h2>
            </div>

            <div className="divide-y divide-gray-100 max-h-[350px] lg:max-h-[400px] overflow-y-auto">
              {loadingActivity ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : recentActivity.length === 0 ? (
                <div className="px-4 lg:px-6 py-8 text-center text-gray-500">
                  No recent activity
                </div>
              ) : recentActivity.map((activity, i) => {
                const isClickable = activity.type === 'application' && activity.applicationId;
                const content = (
                  <>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      activity.type === 'application' ? 'bg-green-100' :
                      activity.type === 'email' ? 'bg-blue-100' :
                      activity.type === 'scan' ? 'bg-purple-100' :
                      'bg-gray-100'
                    }`}>
                      <activity.icon size={14} className={
                        activity.type === 'application' ? 'text-green-600' :
                        activity.type === 'email' ? 'text-blue-600' :
                        activity.type === 'scan' ? 'text-purple-600' :
                        'text-gray-600'
                      } />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900">
                        {activity.type === 'scan' && 'QR code scanned'}
                        {activity.type === 'email' && 'Email inquiry'}
                        {activity.type === 'application' && 'New inquiry'}
                        {activity.type === 'view' && 'Unit viewed'}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {activity.building}
                        {activity.unit && ` • ${activity.unit}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-400 flex-shrink-0">
                      <Clock size={12} />
                      <span className="hidden sm:inline">{activity.time}</span>
                      <span className="sm:hidden">{activity.time.split(' ')[0]}</span>
                    </div>
                  </>
                );

                return isClickable ? (
                  <button
                    key={i}
                    onClick={() => navigate(`/admin/applications?id=${activity.applicationId}`)}
                    className="w-full px-4 lg:px-6 py-3 flex items-start gap-3 hover:bg-gray-50 transition-colors text-left"
                  >
                    {content}
                  </button>
                ) : (
                  <div key={i} className="px-4 lg:px-6 py-3 flex items-start gap-3">
                    {content}
                  </div>
                );
              })}
            </div>

            <div className="px-4 lg:px-6 py-3 border-t border-gray-100">
              <button
                onClick={() => navigate('/admin/analytics')}
                className="text-sm text-primary hover:underline w-full text-center"
              >
                View all activity →
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Add Building Modal */}
      <Modal
        isOpen={isAddBuildingOpen}
        onClose={() => setIsAddBuildingOpen(false)}
        title="Add New Building"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setIsAddBuildingOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateBuilding} disabled={isCreating}>
              {isCreating ? 'Creating...' : 'Create Building'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input
            label="Building Name *"
            placeholder="The Eglinton"
            value={newBuildingData.name}
            onChange={(e) => setNewBuildingData(prev => ({ ...prev, name: e.target.value }))}
          />

          <Input
            label="Address *"
            placeholder="66 Broadway Ave"
            value={newBuildingData.address}
            onChange={(e) => setNewBuildingData(prev => ({ ...prev, address: e.target.value }))}
          />

          <div className="grid grid-cols-2 gap-3">
            <Input
              label="City"
              placeholder="Toronto"
              value={newBuildingData.city}
              onChange={(e) => setNewBuildingData(prev => ({ ...prev, city: e.target.value }))}
            />

            <Input
              label="Postal Code"
              placeholder="M4P 1T6"
              value={newBuildingData.postal_code}
              onChange={(e) => setNewBuildingData(prev => ({ ...prev, postal_code: e.target.value }))}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Leasing Email *"
              type="email"
              placeholder="leasing@beauxproperties.ca"
              value={newBuildingData.leasing_email}
              onChange={(e) => setNewBuildingData(prev => ({ ...prev, leasing_email: e.target.value }))}
            />

            <Input
              label="Leasing Phone"
              type="tel"
              placeholder="647-501-7368"
              value={newBuildingData.leasing_phone}
              onChange={(e) => setNewBuildingData(prev => ({ ...prev, leasing_phone: e.target.value }))}
            />
          </div>

          <p className="text-xs text-gray-500">
            * Required fields. You can add more details after creating the building.
          </p>
        </div>
      </Modal>
    </AdminLayout>
  );
}
